import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { ProposalHcpJobsDto } from '@lib/dto/hcp-job.dto'
import type { HcpJobKind } from '@lib/enums/hcp-job-kind.enum'
import { proposalHcpJobService } from '@/services/ProposalHcpJobService'
import { proposalKeys } from '@/hooks/queryKeys'

/**
 * The Housecall Pro jobs an accepted version becomes. Asked for only where
 * it can mean something: an accepted version, on a deployment connected to
 * HCP.
 */
export function useProposalHcpJobs(
  versionId: string,
  enabled: boolean,
): UseQueryResult<ProposalHcpJobsDto> {
  return useQuery({
    queryKey: proposalKeys.hcpJobs(versionId),
    queryFn: () => proposalHcpJobService.preview(versionId),
    enabled,
  })
}

/**
 * The jobs as the server last answered, and whatever it says next: a failed
 * create can still have left a claim behind (HCP did not answer), so the list
 * is read again whichever way it went.
 */
function useApplyJobs(versionId: string): {
  onSuccess: (jobs: ProposalHcpJobsDto) => void
  onSettled: () => void
} {
  const queryClient = useQueryClient()
  return {
    onSuccess: (jobs) => {
      queryClient.setQueryData(proposalKeys.hcpJobs(versionId), jobs)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: proposalKeys.hcpJobs(versionId) })
    },
  }
}

export interface CreateHcpJobVariables {
  siteId: string
  kind: HcpJobKind
}

/** Creates a job in HCP's live account. Never retried. */
export function useCreateHcpJob(
  versionId: string,
): UseMutationResult<ProposalHcpJobsDto, Error, CreateHcpJobVariables> {
  const apply = useApplyJobs(versionId)
  return useMutation({
    mutationFn: (input: CreateHcpJobVariables) =>
      proposalHcpJobService.create(versionId, input),
    ...apply,
  })
}

export interface SettleHcpJobVariables {
  recordId: string
  /** The job a person found in HCP, or null when it is not there. */
  hcpJobId: string | null
}

export function useSettleHcpJob(
  versionId: string,
): UseMutationResult<ProposalHcpJobsDto, Error, SettleHcpJobVariables> {
  const apply = useApplyJobs(versionId)
  return useMutation({
    mutationFn: ({ recordId, hcpJobId }: SettleHcpJobVariables) =>
      proposalHcpJobService.settle(versionId, recordId, { hcpJobId }),
    ...apply,
  })
}

